/**
 * Role API Client
 *
 * Implements RoleApiClient using tRPC to communicate with the kernel backend.
 * Uses role.* procedures.
 */

import type {
  CreateRoleInput,
  Entitlement,
  Role,
  RoleApiClient,
  RoleWithEntitlements,
  UpdateRoleInput,
} from '@xhubio-saas/core-frontend/plugin-roles'
import { useMemo, useRef } from 'react'
import { trpc } from '@/trpc'

export function useRoleClient(): RoleApiClient {
  const utils = trpc.useUtils()
  const createMutation = trpc.role.create.useMutation()
  const updateMutation = trpc.role.update.useMutation()
  const deleteMutation = trpc.role.delete.useMutation()
  const assignEntitlementsMutation = trpc.role.assignEntitlements.useMutation()
  const removeEntitlementsMutation = trpc.role.removeEntitlements.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({
    createMutation,
    updateMutation,
    deleteMutation,
    assignEntitlementsMutation,
    removeEntitlementsMutation,
  })
  mutations.current = {
    createMutation,
    updateMutation,
    deleteMutation,
    assignEntitlementsMutation,
    removeEntitlementsMutation,
  }

  return useMemo(
    () => ({
      async list(search?: string): Promise<Role[]> {
        await utils.role.list.invalidate()
        const response = await utils.role.list.fetch({ search })
        // biome-ignore lint/suspicious/noExplicitAny: tRPC response is untyped
        return response.map((r: any) => ({
          id: r.id,
          name: r.name,
          description: r.description ?? null,
          organizationId: r.organizationId ?? '',
          isSystem: r.isSystem ?? false,
          isDefault: r.isDefault ?? false,
          createdAt: new Date(r.createdAt),
          updatedAt: new Date(r.updatedAt),
        }))
      },

      async get(roleId: string): Promise<RoleWithEntitlements> {
        await utils.role.get.invalidate({ id: roleId })
        const r = await utils.role.get.fetch({ id: roleId })
        return {
          id: r.id,
          name: r.name,
          description: r.description ?? null,
          organizationId: r.organizationId ?? '',
          isSystem: r.isSystem ?? false,
          isDefault: r.isDefault ?? false,
          createdAt: new Date(r.createdAt),
          updatedAt: new Date(r.updatedAt),
          // biome-ignore lint/suspicious/noExplicitAny: tRPC response is untyped
          entitlements: (r.entitlements ?? []).map((e: any) => ({
            id: e.id,
            key: e.key,
            pluginName: e.pluginName,
            resource: e.resource,
            action: e.action,
            description: e.description ?? null,
            type: e.type ?? 'permission',
          })),
        }
      },

      async create(input: CreateRoleInput): Promise<Role> {
        const r = await mutations.current.createMutation.mutateAsync(input)
        return {
          id: r.id,
          name: r.name,
          description: r.description ?? null,
          organizationId: r.organizationId ?? '',
          isSystem: r.isSystem ?? false,
          isDefault: r.isDefault ?? false,
          createdAt: new Date(r.createdAt),
          updatedAt: new Date(r.updatedAt),
        }
      },

      async update(input: UpdateRoleInput): Promise<Role> {
        const r = await mutations.current.updateMutation.mutateAsync({
          id: input.id,
          name: input.name,
          description: input.description,
        })
        return {
          id: r.id,
          name: r.name,
          description: r.description ?? null,
          organizationId: r.organizationId ?? '',
          isSystem: r.isSystem ?? false,
          isDefault: r.isDefault ?? false,
          createdAt: new Date(r.createdAt),
          updatedAt: new Date(r.updatedAt),
        }
      },

      async delete(roleId: string): Promise<void> {
        await mutations.current.deleteMutation.mutateAsync({ id: roleId })
      },

      async listEntitlements(): Promise<Entitlement[]> {
        await utils.role.listEntitlements.invalidate()
        const response = await utils.role.listEntitlements.fetch({})
        // biome-ignore lint/suspicious/noExplicitAny: tRPC response is untyped
        return response.entitlements.map((e: any) => ({
          id: e.id,
          key: e.key,
          pluginName: e.pluginName,
          resource: e.resource,
          action: e.action,
          description: e.description ?? null,
          type: (e.type ?? 'permission') as 'permission' | 'usage',
        }))
      },

      async getRoleEntitlements(roleId: string): Promise<Entitlement[]> {
        await utils.role.getRoleEntitlements.invalidate({ id: roleId })
        const response = await utils.role.getRoleEntitlements.fetch({ id: roleId })
        // biome-ignore lint/suspicious/noExplicitAny: tRPC response is untyped
        return response.map((e: any) => ({
          id: e.id,
          key: e.key,
          pluginName: e.pluginName,
          resource: e.resource,
          action: e.action,
          description: e.description ?? null,
          type: (e.type ?? 'permission') as 'permission' | 'usage',
        }))
      },

      async assignEntitlements(roleId: string, entitlementIds: string[]): Promise<void> {
        await mutations.current.assignEntitlementsMutation.mutateAsync({ roleId, entitlementIds })
      },

      async removeEntitlements(roleId: string, entitlementIds: string[]): Promise<void> {
        await mutations.current.removeEntitlementsMutation.mutateAsync({ roleId, entitlementIds })
      },
    }),
    // NUR `utils`. Keine Mutationen — sonst ist der Client pro Render neu.
    [utils]
  )
}
